import { API_BASE_URL } from "@/config";
import { useToast } from "@/components/Toast/useToast.js";

export default {
  name: 'WorkflowTemplates',
  setup() {
    const { showSuccess, showError, showInfo } = useToast();

    return {
      showSuccess,
      showError,
      showInfo
    };
  },
  data() {
    return {
      templates: {},
      categories: [],
      activeCategory: 'all',
      loading: true,
      error: null,
      selectedTemplateId: null,
      currentPreviewId: null
    };
  },
  computed: {
    project_id() {
      return this.$store.getters.get_project_id;
    }
  },
  methods: {
    getTemplateDisplayData(template) {
      const name = template?.template_name || template?.name || 'Untitled Template';
      const description = template?.template_description || template?.description || '';
      
      // Handle category as object (new API) or string (legacy)
      const rawCategory = template?.category || template?.template_category || template?.workflow_category || 'custom';
      let categoryName, categoryDisplayName, categoryIcon, categoryId, categoryDescription;
      
      if (typeof rawCategory === 'object' && rawCategory !== null) {
        // New API format: category is an object
        categoryId = rawCategory.id || null;
        categoryName = String(rawCategory.name || 'custom').toLowerCase();
        categoryDisplayName = rawCategory.display_name || this.formatCategoryName(categoryName);
        categoryDescription = rawCategory.description || '';
        categoryIcon = this.normalizeIcon(rawCategory.icon) 
          || this.normalizeIcon(template?.icon) 
          || this.getCategoryIcon(categoryName);
      } else {
        // Legacy format: category is a string
        const normalizedCategory = String(rawCategory || 'custom').toLowerCase();
        const categoryMap = {
          kyb: 'verification',
          credit: 'custom'
        };
        categoryName = categoryMap[normalizedCategory] || normalizedCategory;
        categoryDisplayName = this.formatCategoryName(categoryName);
        categoryIcon = this.normalizeIcon(template?.icon) || this.getCategoryIcon(categoryName);
        categoryId = null;
        categoryDescription = '';
      }
      
      const badge = template?.badge
        || template?.template_badge
        || (template?.is_popular ? 'Popular' : '')
        || (template?.is_recommended ? 'Recommended' : '');
      const services = Array.isArray(template?.services)
        ? template.services
        : Array.isArray(template?.template_services)
          ? template.template_services
          : [];
      const serviceCount = Number.isFinite(template?.services_count)
        ? template.services_count
        : services.length;
      const estCompletionTime = template?.est_completion_time || null;
      const setupTime = estCompletionTime ? `~${estCompletionTime} min` : (template?.setup_time || template?.setupTime || '~10 min');

      return { 
        name, 
        description, 
        category: categoryName, 
        categoryId,
        categoryDisplayName, 
        categoryDescription,
        categoryIcon,
        badge, 
        services, 
        serviceCount, 
        setupTime, 
        icon: categoryIcon 
      };
    },    formatCategoryName(category) {
      const displayNameMap = {
        kyc: 'KYC',
        aml: 'AML',
        onboarding: 'Onboarding',
        verification: 'Verification',
        custom: 'Other'
      };
      return displayNameMap[category] || category.charAt(0).toUpperCase() + category.slice(1);
    },

    normalizeIcon(icon) {
      if (!icon) return null;
      // Ensure icon has bi- prefix for Bootstrap Icons
      return icon.startsWith('bi-') ? icon : `bi-${icon}`;
    },

    getCategoryIcon(category) {
      const iconMap = {
        kyc: 'bi-person-check',
        aml: 'bi-shield-exclamation',
        onboarding: 'bi-person-plus',
        verification: 'bi-patch-check',
        custom: 'bi-gear'
      };
      return iconMap[category] || 'bi-shield-lock';
    },

    extractCategories(templates) {
      const categoryMap = new Map();
      
      Object.values(templates).forEach(template => {
        const rawCategory = template?.category || template?.template_category || template?.workflow_category || 'custom';
        
        if (typeof rawCategory === 'object' && rawCategory !== null) {
          // New API format
          const key = rawCategory.name?.toLowerCase() || 'custom';
          if (!categoryMap.has(key)) {
            categoryMap.set(key, {
              id: rawCategory.id || null,
              name: key,
              displayName: rawCategory.display_name || this.formatCategoryName(key),
              description: rawCategory.description || '',
              icon: this.normalizeIcon(rawCategory.icon) || this.getCategoryIcon(key),
              sortOrder: rawCategory.sort_order ?? 999,
              isActive: rawCategory.is_active !== false,
              count: 0
            });
          }
          categoryMap.get(key).count++;
        } else {
          // Legacy string format
          const normalizedCategory = String(rawCategory || 'custom').toLowerCase();
          const categoryMapLegacy = { kyb: 'verification', credit: 'custom' };
          const key = categoryMapLegacy[normalizedCategory] || normalizedCategory;
          
          if (!categoryMap.has(key)) {
            categoryMap.set(key, {
              id: null,
              name: key,
              displayName: this.formatCategoryName(key),
              description: '',
              icon: this.getCategoryIcon(key),
              sortOrder: 999,
              isActive: true,
              count: 0
            });
          }
          categoryMap.get(key).count++;
        }
      });
      
      // Convert to array and sort by sort_order
      return Array.from(categoryMap.values()).sort((a, b) => a.sortOrder - b.sortOrder);
    },

    fetchTemplates() {
      const TOKEN = localStorage.getItem('token');
      const FETCH_TEMPLATES_API = `${API_BASE_URL}/verify_api/workflow_management_api/get_workflow_templates/`;

      this.loading = true;
      this.error = null;

      $.ajax({
        url: FETCH_TEMPLATES_API,
        method: 'GET',
        contentType: 'application/json',
        headers: {
          Authorization: `Token ${TOKEN}`
        },
        success: (response) => {
          if (response.data && Array.isArray(response.data)) {
            // Convert array to object with id as key for easier access
            this.templates = {};
            response.data.forEach((template) => {
              this.templates[template.id] = template;
            });
            
            // Extract and store unique categories from templates
            this.categories = this.extractCategories(this.templates);
            
            this.loading = false;
            this.$nextTick(() => {
              this.renderTemplates();
              this.updateTemplateCounts();
            });
          } else {
            this.error = 'Invalid response format';
            this.loading = false;
          }
        },
        error: (xhr, status, error) => {
          console.error('Error fetching templates:', error);
          const errorMessage = xhr.responseJSON?.message || 'Failed to load templates. Please try again.';
          this.error = errorMessage;
          this.loading = false;
          this.showError('Error', errorMessage);
        }
      });
    },

    updateTemplateCounts() {
      const displayTemplates = Object.values(this.templates)
        .map(template => this.getTemplateDisplayData(template));

      // Update category counts in the categories array
      this.categories.forEach(cat => {
        cat.count = displayTemplates.filter(template => template.category === cat.name).length;
      });

      // Also update DOM elements if they exist (for backward compatibility)
      document.querySelectorAll('.category-chip').forEach((chip) => {
        const category = chip.dataset.category;
        const countElement = chip.querySelector('.count');
        if (!countElement) return;

        if (category === 'all') {
          countElement.textContent = displayTemplates.length;
          return;
        }

        const count = displayTemplates.filter(template => template.category === category).length;
        countElement.textContent = count;
      });
    },

    renderTemplates() {
      const templatesContainer = document.getElementById('templatesContainer');
      if (!templatesContainer) return;

      templatesContainer.innerHTML = '';

      // Build dynamic grouped templates from extracted categories
      const groupedTemplates = {};
      
      // Initialize groups from dynamic categories
      this.categories.forEach(cat => {
        groupedTemplates[cat.name] = {
          title: cat.displayName,
          icon: cat.icon,
          description: cat.description,
          sortOrder: cat.sortOrder,
          templates: []
        };
      });
      
      // Ensure 'custom' category exists as fallback
      if (!groupedTemplates['custom']) {
        groupedTemplates['custom'] = {
          title: 'Other',
          icon: this.getCategoryIcon('custom'),
          description: '',
          sortOrder: 999,
          templates: []
        };
      }

      // Sort templates into categories
      Object.values(this.templates).forEach(template => {
        const display = this.getTemplateDisplayData(template);
        const category = groupedTemplates[display.category] ? display.category : 'custom';
        groupedTemplates[category].templates.push({ template, display });
      });

      // Sort category entries by sortOrder and render each category section
      const sortedCategories = Object.entries(groupedTemplates)
        .sort(([, a], [, b]) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999));

      sortedCategories.forEach(([categoryKey, categoryData]) => {
        if (categoryData.templates.length === 0) return;

        const sectionHtml = `
          <div class="category-section" data-category-section="${categoryKey}">
            <div class="category-header">
              <h3 class="category-title">${categoryData.title}</h3>
              <span class="category-count">${categoryData.templates.length}</span>
            </div>
            <div class="templates-grid" data-category-grid="${categoryKey}">
              ${categoryData.templates.map(({ template, display }) => {
                const badgeHtml = display.badge
                  ? `<span class="template-badge ${display.badge.toLowerCase()}">${display.badge}</span>`
                  : '';
                const serviceCount = display.serviceCount;

                return `
                  <div class="template-card" data-id="${template.id}" data-category="${display.category}">
                    <div class="selected-check"><i class="bi bi-check-lg"></i></div>
                    <div class="template-header">
                      <div class="template-icon ${display.category}"><i class="bi ${display.icon}"></i></div>
                      <div class="template-title-section">
                        <div class="template-title">${display.name}</div>
                        ${badgeHtml}
                      </div>
                    </div>
                    <p class="template-description">
                      ${display.description}
                    </p>
                    <div class="template-meta">
                      <div class="meta-item">
                        <i class="bi bi-clipboard-check meta-icon"></i>
                        <strong>${serviceCount}</strong> Services
                      </div>
                      <div class="meta-item">
                        <i class="bi bi-clock meta-icon"></i>
                        ${display.setupTime} est. completion
                      </div>
                      <div class="meta-item clickable wf-tooltip" data-tooltip="Click to preview">
                        <i class="bi bi-eye meta-icon"></i>
                        Preview
                      </div>
                    </div>
                  </div>
                `;
              }).join('')}
            </div>
          </div>
        `;

        templatesContainer.innerHTML += sectionHtml;
      });

      // Re-attach Vue event listeners
      this.$nextTick(() => {
        document.querySelectorAll('.template-card').forEach((card) => {
          const templateId = parseInt(card.dataset.id);
          card.addEventListener('click', (e) => {
            if (!e.target.closest('.clickable')) {
              this.selectTemplate(card, templateId);
            }
          });
        });

        document.querySelectorAll('.meta-item.clickable').forEach((item) => {
          item.addEventListener('click', (e) => {
            e.stopPropagation();
            const card = item.closest('.template-card');
            const templateId = parseInt(card.dataset.id);
            this.previewTemplate(templateId);
          });
        });
      });
    },

    selectTemplate(element, templateId) {
      document.querySelectorAll('.template-card').forEach(card => {
        card.classList.remove('selected');
      });

      element.classList.add('selected');
      this.selectedTemplateId = templateId;

      const continueBtn = document.getElementById('continueBtn');
      const selectionText = document.getElementById('selectionText');
      const selectedPreview = document.getElementById('selectedPreview');
      const selectedTemplateName = document.getElementById('selectedTemplateName');

      if (continueBtn) continueBtn.disabled = false;
      if (selectionText) selectionText.style.display = 'none';
      if (selectedPreview) selectedPreview.style.display = 'flex';

      if (templateId === 0) {
        if (selectedTemplateName) selectedTemplateName.textContent = 'Start from Scratch';
      } else {
        const display = this.getTemplateDisplayData(this.templates[templateId]);
        if (selectedTemplateName) selectedTemplateName.textContent = display.name;
      }
    },

    previewTemplate(templateId) {
      this.currentPreviewId = templateId;
      const template = this.templates[templateId];
      const display = this.getTemplateDisplayData(template);

      const modalTitle = document.getElementById('modalTitle');
      const modalDescription = document.getElementById('modalDescription');
      const modalCategoryBadge = document.getElementById('modalCategoryBadge');
      const modalCategoryDescription = document.getElementById('modalCategoryDescription');
      const multipleOnboarding = document.getElementById('multipleOnboarding');
      const batchOnboarding = document.getElementById('batchOnboarding');
      const servicesList = document.getElementById('servicesList');
      const previewModal = document.getElementById('previewModal');

      if (modalTitle) modalTitle.textContent = display.name;
      if (modalDescription) modalDescription.textContent = display.description;
      
      // Update category badge with icon and display name
      if (modalCategoryBadge) {
        modalCategoryBadge.innerHTML = `<i class="bi ${display.categoryIcon}"></i> ${display.categoryDisplayName}`;
        modalCategoryBadge.className = `modal-category-badge ${display.category}`;
      }
      
      // Show category description if available
      if (modalCategoryDescription) {
        if (display.categoryDescription) {
          modalCategoryDescription.textContent = display.categoryDescription;
          modalCategoryDescription.style.display = 'block';
        } else {
          modalCategoryDescription.style.display = 'none';
        }
      }
      if (multipleOnboarding) multipleOnboarding.textContent = template?.settings?.multipleOnboarding ? 'Enabled' : 'Disabled';
      if (batchOnboarding) batchOnboarding.textContent = template?.settings?.batchOnboarding ? 'Enabled' : 'Disabled';

      if (servicesList) {
        servicesList.innerHTML = display.services.map((service, index) => {
          const serviceName = service?.name || service?.service_name || 'Service';
          const serviceType = service?.type || service?.service_type || '';
          const isRequired = service?.required || service?.is_required;
          const isCustom = service?.custom || service?.is_custom;
          return `
          <div class="service-item">
            <div class="service-order">${index + 1}</div>
            <div class="service-info">
              <div class="service-name">${serviceName}</div>
              <div class="service-type">${serviceType}</div>
            </div>
            <div class="service-badges">
              ${isRequired ? '<span class="service-badge required">Required</span>' : ''}
              ${isCustom ? '<span class="service-badge custom">Custom</span>' : ''}
            </div>
          </div>
        `;
        }).join('');
      }

      if (previewModal) previewModal.classList.add('active');
    },

    closeModal() {
      const previewModal = document.getElementById('previewModal');
      if (previewModal) previewModal.classList.remove('active');
    },

    selectAndContinue() {
      const card = document.querySelector(`.template-card[data-id="${this.currentPreviewId}"]`);
      this.selectTemplate(card, this.currentPreviewId);
      this.closeModal();
      this.create();
    },

    filterByCategory(category, e) {
      // Update active category state
      this.activeCategory = category;
      
      document.querySelectorAll('.category-chip').forEach(chip => {
        chip.classList.remove('active');
      });
      const target = e?.currentTarget;
      if (target) {
        target.classList.add('active');
      }

      // Show/hide category sections
      document.querySelectorAll('.category-section').forEach(section => {
        const sectionCategory = section.dataset.categorySection;
        if (category === 'all' || sectionCategory === category) {
          section.style.display = 'block';
        } else {
          section.style.display = 'none';
        }
      });
    },

    filterTemplates() {
      const searchInput = document.getElementById('searchInput');
      const searchValue = searchInput ? searchInput.value.toLowerCase() : '';

      document.querySelectorAll('.category-section').forEach(section => {
        let visibleCards = 0;

        section.querySelectorAll('.template-card').forEach(card => {
          const title = card.querySelector('.template-title');
          const description = card.querySelector('.template-description');
          const titleText = title ? title.textContent.toLowerCase() : '';
          const descriptionText = description ? description.textContent.toLowerCase() : '';

          if (titleText.includes(searchValue) || descriptionText.includes(searchValue)) {
            card.style.display = 'block';
            visibleCards++;
          } else {
            card.style.display = 'none';
          }
        });

        // Hide section if no cards are visible
        section.style.display = visibleCards > 0 ? 'block' : 'none';
      });
    },

    toggleFilters() {
      this.showInfo('Info', 'Filter panel coming soon!');
    },

    async create() {
      const TOKEN = localStorage.getItem('token');
      const projectId = this.project_id;

      if (!projectId) {
        this.showError('Error', 'Missing project context. Please reopen from the project dashboard.');
        return;
      }

      if (this.selectedTemplateId === null || this.selectedTemplateId === undefined) {
        this.showError('Error', 'Please select a template first');
        return;
      }

      const FETCH_TEMPLATES_API =
        `${API_BASE_URL}/verify_api/workflow_management_api/create_from_template/${projectId}/`;

      try {
        const response = await fetch(FETCH_TEMPLATES_API, {
          method: 'POST',
          headers: {
            'Authorization': `Token ${TOKEN}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            template_id: this.selectedTemplateId
          })
        });

        const data = await response.json();

        if (!response.ok || data.status === 'error') {
          const errorMessage = data.message || 'Failed to create workflow';
          this.showError('Error', errorMessage);
          return;
        }

        // 👇 side effects stay here, not in .then()
        this.showSuccess('Success', 'Workflow created successfully!');
        this.$router.push({ name: 'project_dashboard' });

      } catch (error) {
        console.error('Error creating workflow:', error);
        this.showError('Error', 'Failed to create workflow');
      }
    },

    goBack() {
      this.$router.back();
    },

  },
  mounted() {
    this.fetchTemplates();

    const previewModal = document.getElementById('previewModal');
    if (previewModal) {
      previewModal.addEventListener('click', (e) => {
        if (e.target === previewModal) {
          this.closeModal();
        }
      });
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeModal();
      }
    });
  }
};
